






































































































































import Vue from 'vue';
import axios from 'axios';

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const GRID_SIZE = 10;

interface TextElement {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    fontSize: number;
    bold: boolean;
    italic: boolean;
    underline: boolean;
    align: 'left' | 'center' | 'right';
    color: string;
    content: string;
}

interface FieldElement {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    name: string;
    type: 'Text' | 'Checkbox' | 'Dropdown';
    options?: string[];
    label: string;
    labelPosition: 'top' | 'left';
    labelFontSize: number;
    labelBold: boolean;
    labelItalic: boolean;
    labelUnderline: boolean;
    labelColor: string;
}

interface ImageElement {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    dataUrl: string;
}

const LABEL_FONT_SIZE = 12;
const LABEL_GAP = 4;
// Kept in sync with the same limit enforced server-side in filler.ts -
// images ride along as base64 in the save payload, so a large one bloats
// both that request and the generated PDF.
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export default Vue.extend({
    name: 'FillerBetaDesigner',
    data() {
        return {
            name: '',
            textElements: [] as TextElement[],
            fieldElements: [] as FieldElement[],
            imageElements: [] as ImageElement[],
            selectedId: null as number | null,
            selectedKind: null as 'text' | 'field' | 'image' | null,
            nextId: 1,
            fieldCounter: 1,
            saving: false,
            snapEnabled: true,
            addMenuOpen: false
        }
    },
    computed: {
        labeledFields(): FieldElement[] {
            return this.fieldElements.filter(el => el.label);
        },
        selected(): TextElement | FieldElement | ImageElement | null {
            if(!this.selectedId) return null;
            const list: any[] = this.selectedKind === 'text' ? this.textElements
                : this.selectedKind === 'image' ? this.imageElements
                : this.fieldElements;
            return list.find(el => el.id === this.selectedId) || null;
        },
        optionsText: {
            get(): string {
                const sel = this.selected as FieldElement | null;
                return sel && sel.options ? sel.options.join('\n') : '';
            },
            set(value: string) {
                const sel = this.selected as FieldElement | null;
                if(sel) {
                    sel.options = value.split('\n').map(s => s.trim()).filter(Boolean);
                }
            }
        }
    },
    created() {
        this.name = '' + this.$route.params.name;
        this.$store.state.routeLoaded = true;
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideAddMenuClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideAddMenuClick);
    },
    methods: {
        isSelected(el: any, kind: string): boolean {
            return this.selectedKind === kind && this.selectedId === el.id;
        },
        select(el: any, kind: 'text' | 'field' | 'image') {
            this.selectedId = el.id;
            this.selectedKind = kind;
        },
        deselect() {
            this.selectedId = null;
            this.selectedKind = null;
        },
        toggleAddMenu() {
            this.addMenuOpen = !this.addMenuOpen;
        },
        chooseAdd(kind: 'text' | 'image' | 'field') {
            this.addMenuOpen = false;
            if(kind === 'text') this.addText();
            else if(kind === 'field') this.addField();
            else if(kind === 'image') (this.$refs.imageInput as HTMLInputElement).click();
        },
        handleOutsideAddMenuClick(event: MouseEvent) {
            const menu = this.$refs.addMenu as HTMLElement;
            if(this.addMenuOpen && menu && !menu.contains(event.target as Node)) {
                this.addMenuOpen = false;
            }
        },
        onImageFileChange(event: Event) {
            const input = event.target as HTMLInputElement;
            const file = input.files && input.files[0];
            input.value = '';
            if(!file) return;
            if(!['image/png', 'image/jpeg'].includes(file.type)) {
                alert('Only PNG and JPEG images are supported.');
                return;
            }
            if(file.size > MAX_IMAGE_BYTES) {
                alert(`That image is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Please use an image under ${MAX_IMAGE_BYTES / (1024 * 1024)}MB.`);
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result as string;
                const img = new Image();
                img.onload = () => {
                    const maxWidth = 200;
                    const scale = img.width > maxWidth ? maxWidth / img.width : 1;
                    const el: ImageElement = {
                        id: this.nextId++,
                        x: 50,
                        y: 50,
                        width: Math.round(img.width * scale),
                        height: Math.round(img.height * scale),
                        dataUrl
                    };
                    this.imageElements.push(el);
                    this.select(el, 'image');
                };
                img.src = dataUrl;
            };
            reader.readAsDataURL(file);
        },
        addText() {
            const el: TextElement = {
                id: this.nextId++,
                x: 50,
                y: 50,
                width: 200,
                height: 30,
                fontSize: 14,
                bold: false,
                italic: false,
                underline: false,
                align: 'left',
                color: '#000000',
                content: 'New Text'
            };
            this.textElements.push(el);
            this.select(el, 'text');
        },
        addField() {
            // Defaults to a plain Text field - the type can be changed
            // afterward in the properties panel, so there's no need to ask
            // upfront.
            const el: FieldElement = {
                id: this.nextId++,
                x: 50,
                y: 50,
                width: 160,
                height: 24,
                name: `field_${this.fieldCounter++}`,
                type: 'Text',
                label: '',
                labelPosition: 'top',
                labelFontSize: 12,
                labelBold: false,
                labelItalic: false,
                labelUnderline: false,
                labelColor: '#000000'
            };
            this.fieldElements.push(el);
            this.select(el, 'field');
        },
        onFieldTypeChange() {
            const sel = this.selected as FieldElement | null;
            if(!sel) return;
            if(sel.type === 'Dropdown' && (!sel.options || sel.options.length === 0)) {
                sel.options = ['Option 1', 'Option 2'];
            }
        },
        deleteSelected() {
            if(!this.selected) return;
            if(this.selectedKind === 'text') {
                this.textElements = this.textElements.filter(el => el.id !== this.selectedId);
            } else if(this.selectedKind === 'image') {
                this.imageElements = this.imageElements.filter(el => el.id !== this.selectedId);
            } else {
                this.fieldElements = this.fieldElements.filter(el => el.id !== this.selectedId);
            }
            this.deselect();
        },
        clamp(value: number, min: number, max: number): number {
            return Math.min(Math.max(value, min), Math.max(min, max));
        },
        snapValue(value: number): number {
            return this.snapEnabled ? Math.round(value / GRID_SIZE) * GRID_SIZE : value;
        },
        fieldLabelStyle(el: FieldElement): any {
            const fontSize = el.labelFontSize || LABEL_FONT_SIZE;
            const textStyle = {
                fontSize: fontSize + 'px',
                fontWeight: el.labelBold ? 'bold' : 'normal',
                fontStyle: el.labelItalic ? 'italic' : 'normal',
                textDecoration: el.labelUnderline ? 'underline' : 'none',
                color: el.labelColor
            };
            if(el.labelPosition === 'left') {
                // A div's line box has leading above/below the glyph, so
                // centering by fontSize/2 alone lands a bit high. Matching
                // the label's own top/height to the field's and centering
                // with flex sidesteps font-metric guesswork entirely.
                return {
                    ...textStyle,
                    position: 'absolute',
                    right: (PAGE_WIDTH - el.x + LABEL_GAP) + 'px',
                    top: el.y + 'px',
                    height: el.height + 'px',
                    display: 'flex',
                    alignItems: 'center'
                };
            }
            return {
                ...textStyle,
                position: 'absolute',
                left: el.x + 'px',
                top: Math.max(0, el.y - LABEL_GAP - fontSize) + 'px'
            };
        },
        startDrag(el: TextElement | FieldElement | ImageElement, kind: 'text' | 'field' | 'image', event: MouseEvent) {
            event.preventDefault();
            this.select(el, kind);
            const startX = event.clientX;
            const startY = event.clientY;
            const originX = el.x;
            const originY = el.y;
            const maxX = PAGE_WIDTH - el.width;
            const maxY = PAGE_HEIGHT - el.height;
            const onMove = (e: MouseEvent) => {
                el.x = this.clamp(this.snapValue(originX + (e.clientX - startX)), 0, maxX);
                el.y = this.clamp(this.snapValue(originY + (e.clientY - startY)), 0, maxY);
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        startResize(el: TextElement | FieldElement | ImageElement, event: MouseEvent) {
            event.preventDefault();
            event.stopPropagation();
            const startX = event.clientX;
            const startY = event.clientY;
            const originW = el.width;
            const originH = el.height;
            const onMove = (e: MouseEvent) => {
                el.width = this.clamp(this.snapValue(originW + (e.clientX - startX)), 10, PAGE_WIDTH - el.x);
                el.height = this.clamp(this.snapValue(originH + (e.clientY - startY)), 10, PAGE_HEIGHT - el.y);
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        save() {
            if(this.saving) return;

            for(const el of this.fieldElements) {
                if(!el.name || !el.name.trim()) {
                    alert('Every field needs a name.');
                    return;
                }
                if(el.type === 'Dropdown' && (!el.options || el.options.length === 0)) {
                    alert(`Dropdown field "${el.name}" needs at least one option.`);
                    return;
                }
            }
            const names = this.fieldElements.map(el => el.name);
            if(new Set(names).size !== names.length) {
                alert('Field names must be unique.');
                return;
            }

            this.saving = true;
            axios.post(this.$store.state.apiUrl + '/filler/template', {
                name: this.name,
                contactProps: JSON.stringify([]),
                companyProps: JSON.stringify([]),
                isNew: 'true',
                design: {
                    textElements: this.textElements.map(el => ({ x: el.x, y: el.y, width: el.width, height: el.height, fontSize: el.fontSize, bold: el.bold, italic: el.italic, underline: el.underline, align: el.align, color: el.color, content: el.content })),
                    fieldElements: this.fieldElements.map(el => ({ x: el.x, y: el.y, width: el.width, height: el.height, name: el.name, type: el.type, options: el.options, label: el.label, labelPosition: el.labelPosition, labelFontSize: el.labelFontSize, labelBold: el.labelBold, labelItalic: el.labelItalic, labelUnderline: el.labelUnderline, labelColor: el.labelColor })),
                    imageElements: this.imageElements.map(el => ({ x: el.x, y: el.y, width: el.width, height: el.height, dataUrl: el.dataUrl }))
                }
            })
                .then(() => {
                    this.saving = false;
                    this.$router.push('/filler-beta/' + encodeURIComponent(this.name));
                })
                .catch(e => {
                    alert(e.response ? e.response.data : 'There was an error.');
                    this.saving = false;
                });
        }
    }
})
